// src/lib/taglessPolicy.js — 비콘 태그리스 정책(순수 · Firebase import 금지 · 2026-10-11)
// ---------------------------------------------------------------------------
// 🔴 이 파일은 서버 정본 `functions/taglessPolicy.js` 의 **거울**이다. 상수가 갈리면
//    «앱은 60초 머물렀다고 부르는데 서버는 거부» 가 되고 승객은 이유를 모른다
//    (2026-09-01 승객 초기 PIN 사고와 같은 «값을 만드는 곳 ≠ 판정하는 곳»).
//    `scripts/test_tagless_policy.cjs` 가 두 파일의 상수·판정을 대조한다 — 한쪽만 고치면 게이트가 빨개진다.
// 🔴 dwell 없이 «감지 즉시 탑승» 금지 — 정류장에서 다른 버스가 지나가면 오탑승한다.
// ---------------------------------------------------------------------------

export const BUSLINK_BEACON_UUID = "8C31946E-F97A-4060-8774-96D30275129A";
export const TAGLESS_DWELL_SEC = 60;
export const TAGLESS_MIN_RSSI = -85;
export const BEACON_ID_MAX = 65535;

// ── 클라 전용(서버에는 없다) ──
// 비콘을 이 시간 넘게 못 보면(또는 신호가 약하면) «연속» 이 끊긴 것으로 친다.
// ranging 은 1초 남짓 간격이라 한두 번 빠지는 건 흔하다 — 0 으로 두면 버스 안에서도 영영 60초가 안 찬다.
export const TAGLESS_GAP_SEC = 10;
// 한 번 탑승을 부른 뒤 이 시간 동안은 다시 부르지 않는다(서버도 멱등이지만 호출 자체를 줄인다).
export const TAGLESS_REFIRE_SEC = 600;

/** 거래처가 태그리스를 켰는가. 🔴 부재·모르는 값 = 꺼짐. 서버 isTaglessEnabled 와 같은 판정. */
export function isTaglessEnabled(codeData) {
  return !!(codeData && codeData.tagless === true);
}

/** major/minor 1개 → 0~65535 정수 또는 null. 서버 normalizeBeaconId 와 같은 판정. */
export function normalizeBeaconId(v) {
  const n = typeof v === "number" ? v : (typeof v === "string" && /^\s*\d+\s*$/.test(v) ? Number(v) : NaN);
  if (!Number.isInteger(n) || n < 0 || n > BEACON_ID_MAX) return null;
  return n;
}

/**
 * 같은 비콘을 연속으로 본 시간을 잰다(순수 · 시계는 주입).
 * 사용: const t = createDwellTracker(); 그리고 ranging 이벤트마다 t.feed(beacons, nowMs)
 *   → 조건을 채운 비콘이 있으면 {major, minor, rssi, dwellSec} 1개, 아니면 null.
 * 조건 = rssi ≥ TAGLESS_MIN_RSSI 인 관측이 TAGLESS_GAP_SEC 넘게 끊기지 않고 TAGLESS_DWELL_SEC 이상.
 * 둘 이상이 동시에 차면 평균 신호가 센 쪽 하나만(옆 버스와 나란히 선 차고지 대비).
 * 한 번 돌려준 뒤 TAGLESS_REFIRE_SEC 동안은 아무것도 돌려주지 않는다.
 */
export function createDwellTracker(opts = {}) {
  const dwellMs = (opts.dwellSec ?? TAGLESS_DWELL_SEC) * 1000;
  const gapMs = (opts.gapSec ?? TAGLESS_GAP_SEC) * 1000;
  const refireMs = (opts.refireSec ?? TAGLESS_REFIRE_SEC) * 1000;
  const minRssi = opts.minRssi ?? TAGLESS_MIN_RSSI;
  const seen = new Map(); // key → { major, minor, first, last, sum, n }
  let lastFire = -Infinity;
  return {
    feed(beacons, nowMs) {
      const list = Array.isArray(beacons) ? beacons : [];
      for (const b of list) {
        if (!b) continue;
        if (b.uuid && String(b.uuid).toUpperCase() !== BUSLINK_BEACON_UUID) continue;
        const major = normalizeBeaconId(b.major);
        const minor = normalizeBeaconId(b.minor);
        const rssi = Number(b.rssi);
        // rssi 0 은 iOS 가 «이번엔 못 쟀다» 로 주는 값 — 강한 신호로 세지 않는다.
        if (major === null || minor === null || !Number.isFinite(rssi) || rssi >= 0 || rssi < minRssi) continue;
        const key = major + "-" + minor;
        const cur = seen.get(key);
        if (cur && nowMs - cur.last <= gapMs) {
          cur.last = nowMs; cur.sum += rssi; cur.n += 1;
        } else {
          seen.set(key, { major, minor, first: nowMs, last: nowMs, sum: rssi, n: 1 });
        }
      }
      // 끊긴 것 정리
      for (const [k, v] of seen) if (nowMs - v.last > gapMs) seen.delete(k);
      if (nowMs - lastFire < refireMs) return null;
      let best = null;
      for (const v of seen.values()) {
        if (v.last !== nowMs) continue;              // 이번 관측에 없던 비콘은 «지금 안» 이 아니다
        if (nowMs - v.first < dwellMs) continue;
        const avg = v.sum / v.n;
        if (!best || avg > best.avg) best = { ...v, avg };
      }
      if (!best) return null;
      lastFire = nowMs;
      return {
        major: best.major, minor: best.minor,
        rssi: Math.round(best.avg),
        dwellSec: Math.floor((nowMs - best.first) / 1000),
      };
    },
    reset() { seen.clear(); },
  };
}
